import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaUser,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaLanguage,
  FaUserTag,
  FaClock,
  FaMoneyBillWave,
  FaInfoCircle,
  FaCheckCircle,
  FaHandsHelping,
  FaBaby,
  FaChild,
  FaShieldAlt,
} from "react-icons/fa";
import config, { getUserCity, fetchRemoteConfig } from "../../../config";
import TermsAcceptStep from "./TermsAcceptStep";

const BASE_URL = config.API_BASE_URL || "http://localhost:8000";

// Child Type -> which checklist ("form") applies. This is the "Age ->
// Baby Form / Child Form" branch: Infant/Baby, Toddler and PreSchooler all
// use the Baby Care checklist (feeding/play/routine fits a 4-6 year old
// better than homework/school-prep), Adolescent/School-Going Child uses
// the Child Care checklist — Safety is common to both.
const CHILD_TYPE_OPTIONS = [
  { value: "Infant/Baby",                    age: "1-12 months" },
  { value: "Toddler",                        age: "2-3 years" },
  { value: "PreSchooler",                    age: "4-6 years" },
  { value: "Adolescent/School-Going Child",  age: "7-15 years" },
];
const BABY_BUCKET_TYPES = ["Infant/Baby", "Toddler", "PreSchooler"];

const PARENT_GUARDIAN_RELATIONS = ["Mother", "Father", "Grandparent", "Legal Guardian", "Other"];

export const BABY_CARE_OPTIONS = [
  { value: "diaper-changing", label: "Diaper Changing" },
  { value: "baby-bathing", label: "Baby Bathing" },
  { value: "baby-hygiene", label: "Baby Hygiene" },
  { value: "baby-dressing", label: "Dressing" },
  { value: "baby-feeding", label: "Feeding" },
  { value: "bottle-feeding", label: "Bottle Feeding" },
  { value: "burping", label: "Burping" },
  { value: "sleep-supervision", label: "Sleep Supervision" },
  { value: "baby-routine", label: "Baby Routine" },
  { value: "baby-safe-handling", label: "Safe Handling" },
  { value: "age-appropriate-play", label: "Age-Appropriate Play" },
  { value: "baby-area-organization", label: "Baby Area Organization" },
];

export const CHILD_CARE_OPTIONS = [
  { value: "child-bathing", label: "Bathing" },
  { value: "child-dressing", label: "Dressing" },
  { value: "grooming", label: "Grooming" },
  { value: "child-feeding", label: "Feeding" },
  { value: "meal-serving", label: "Meal Serving" },
  { value: "school-preparation", label: "School Preparation" },
  { value: "homework", label: "Homework" },
  { value: "reading", label: "Reading" },
  { value: "indoor-play", label: "Indoor Play" },
  { value: "outdoor-play", label: "Outdoor Play" },
  { value: "creative-activities", label: "Creative Activities" },
  { value: "educational-activities", label: "Educational Activities" },
  { value: "child-daily-routine", label: "Daily Routine" },
];

export const SAFETY_OPTIONS = [
  { value: "continuous-supervision", label: "Continuous Supervision" },
  { value: "safe-play", label: "Safe Play" },
  { value: "safety-safe-handling", label: "Safe Handling" },
  { value: "activity-monitoring", label: "Activity Monitoring" },
];

const BabyCaretaker = () => {
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [step, setStep] = useState("form"); // "form" -> "terms"

  const categoryKey = config.HOME_SERVICES_CATEGORY || "care-services";
  const serviceKey = config.BABY_CARETAKER_SERVICE || "baby-caretaker";

  // City/Area come from the admin-configured backend list (config_data
  // collection), not a hardcoded array — see the same pattern's comment
  // in ElderCare.js for why this re-syncs on mount instead of trusting
  // the module-level default.
  const [areaOptions, setAreaOptions] = useState(config.AREA_OPTIONS || {});
  useEffect(() => {
    fetchRemoteConfig().then(() => setAreaOptions({ ...config.AREA_OPTIONS }));
  }, []);
  const selectedCategory = config.SERVICE_CONFIG?.[categoryKey];
  const selectedService =
	config.SERVICE_CONFIG?.[categoryKey]?.services?.[serviceKey];

  const serviceLabel = selectedService?.label || "Baby Caretaker";
  const categoryLabel = selectedCategory?.label || "Care Services";

  const [formData, setFormData] = useState({
	// Requester's own contact details
	username: "",
	mobilenumber: "",
	email: "",
	address: "",
	city: "",
	area: "",
	language: "",

	// About the child
	childName: "",
	childAge: "",
	childGender: "",
	childType: "",
	parentGuardianName: "",
	parentGuardianRelation: "",

	// Care services required
	service_type: [],

	// Timing & caretaker preference
	gender: "",
	urgencyStatus: "",
	duration: "",
	shiftDuration: "",
	shiftTime: "",
	experienceLevel: "",
	additionalNotes: "",
	salaryOffered: "",
  });

  const [showExperienceInfo, setShowExperienceInfo] = useState(false);

  // "Important coding logic": Age (via Child Type) -> Baby Form or Child
  // Form. Baby/Infant/Toddler show the Baby Care checklist; School-going
  // Child shows the Child Care checklist. Safety is common to both, once
  // a bucket is known.
  const careBucket = BABY_BUCKET_TYPES.includes(formData.childType)
	? "baby"
	: formData.childType === "Adolescent/School-Going Child"
	  ? "child"
	  : null;

  const availableAreas = useMemo(() => {
	const selected =
	  areaOptions[formData.city] || areaOptions[formData.city?.toLowerCase()];
	if (Array.isArray(selected)) return selected;
	if (selected?.areas) return selected.areas;
	return [];
  }, [formData.city, areaOptions]);

  const styles = {
	page: {
	  minHeight: "100vh",
	  background:
		"linear-gradient(135deg, #eff6ff 0%, #dbeafe 50%, #eef2ff 100%)",
	  padding: "30px 20px",
	  fontFamily: "Segoe UI, Tahoma, Geneva, Verdana, sans-serif",
	},
	wrapper: {
	  maxWidth: "1580px",
	  width: "96%",
	  margin: "0 auto",
	},
	hero: {
	  background: "linear-gradient(135deg, #0f172a, #1d4ed8, #38bdf8)",
	  color: "#fff",
	  borderRadius: "28px",
	  padding: "34px 36px",
	  boxShadow: "0 20px 40px rgba(37, 99, 235, 0.22)",
	  marginBottom: "24px",
	},
	heroBadge: {
	  display: "inline-block",
	  background: "rgba(255,255,255,0.14)",
	  padding: "7px 14px",
	  borderRadius: "999px",
	  fontSize: "12px",
	  fontWeight: "700",
	  marginBottom: "12px",
	},
	heroTitle: {
	  margin: 0,
	  fontSize: "2.1rem",
	  fontWeight: "800",
	},
	heroText: {
	  marginTop: "10px",
	  marginBottom: 0,
	  color: "rgba(255,255,255,0.9)",
	  maxWidth: "900px",
	  lineHeight: 1.6,
	  fontSize: "15px",
	},
	formCard: {
	  background: "#fff",
	  borderRadius: "28px",
	  border: "1px solid #e5edf8",
	  boxShadow: "0 14px 34px rgba(15, 23, 42, 0.08)",
	  overflow: "hidden",
	  width: "100%",
	  maxWidth: "1320px",
	  margin: "0 auto",
	},
	formHeader: {
	  padding: "28px 40px",
	  borderBottom: "1px solid #edf2f7",
	  background: "linear-gradient(180deg, #ffffff, #f8fbff)",
	},
	formTitle: {
	  margin: 0,
	  fontSize: "1.6rem",
	  fontWeight: "800",
	  color: "#0f172a",
	},
	formSub: {
	  margin: "8px 0 0 0",
	  color: "#64748b",
	  fontSize: "14px",
	},
	body: {
	  padding: "32px 40px 36px",
	},
	section: {
	  marginBottom: "28px",
	  padding: "24px",
	  borderRadius: "20px",
	  background: "#f8fbff",
	  border: "1px solid #e6eef8",
	},
	sectionTitle: {
	  margin: "0 0 18px 0",
	  fontSize: "1.1rem",
	  fontWeight: "800",
	  color: "#0f172a",
	  display: "flex",
	  alignItems: "center",
	  gap: "8px",
	},
	grid: {
	  display: "grid",
	  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
	  gap: "20px",
	  alignItems: "start",
	},
	field: {
	  display: "flex",
	  flexDirection: "column",
	  gap: "8px",
	},
	label: {
	  fontSize: "14px",
	  fontWeight: "700",
	  color: "#334155",
	  display: "flex",
	  alignItems: "center",
	  gap: "8px",
	},
	input: {
	  width: "100%",
	  padding: "14px 16px",
	  borderRadius: "14px",
	  border: "1px solid #d6deeb",
	  fontSize: "15px",
	  outline: "none",
	  background: "#fff",
	  boxSizing: "border-box",
	},
	select: {
	  width: "100%",
	  padding: "14px 16px",
	  borderRadius: "14px",
	  border: "1px solid #d6deeb",
	  fontSize: "15px",
	  outline: "none",
	  background: "#fff",
	  boxSizing: "border-box",
	},
	infoBtn: {
	  border: "none",
	  background: "none",
	  padding: 0,
	  margin: 0,
	  color: "#2563eb",
	  fontSize: "15px",
	  cursor: "pointer",
	  display: "inline-flex",
	  alignItems: "center",
	},
	infoPopover: {
	  position: "absolute",
	  top: "calc(100% + 6px)",
	  left: 0,
	  right: 0,
	  zIndex: 5,
	  background: "#fff",
	  border: "1px solid #d6deeb",
	  borderRadius: "12px",
	  boxShadow: "0 10px 28px rgba(15, 23, 42, 0.14)",
	  padding: "14px 16px",
	},
	textarea: {
	  width: "100%",
	  minHeight: "120px",
	  padding: "14px 16px",
	  borderRadius: "14px",
	  border: "1px solid #d6deeb",
	  fontSize: "15px",
	  outline: "none",
	  background: "#fff",
	  boxSizing: "border-box",
	  resize: "vertical",
	},
	submitWrap: {
	  display: "flex",
	  justifyContent: "center",
	  marginTop: "30px",
	},
	submitBtn: {
	  border: "none",
	  borderRadius: "999px",
	  background: "linear-gradient(135deg, #1d4ed8, #2563eb)",
	  color: "#fff",
	  padding: "15px 34px",
	  fontSize: "15px",
	  fontWeight: "800",
	  minWidth: "260px",
	  boxShadow: "0 12px 24px rgba(37, 99, 235, 0.24)",
	  cursor: "pointer",
	},
	helper: {
	  color: "#64748b",
	  fontSize: "13px",
	  margin: 0,
	  lineHeight: 1.6,
	},
	serviceGrid: {
	  display: "grid",
	  gridTemplateColumns: "repeat(2, 1fr)",
	  gap: "10px",
	  marginTop: "10px",
	},
	serviceCell: {
	  display: "flex",
	  alignItems: "center",
	  gap: "8px",
	  padding: "10px 14px",
	  borderRadius: "12px",
	  border: "1px solid #dbe7f5",
	  background: "#fff",
	},
	serviceCellLabel: {
	  fontSize: "14px",
	  fontWeight: "600",
	  color: "#0f172a",
	},
	groupHeading: {
	  fontSize: "13px",
	  fontWeight: "800",
	  color: "#1d4ed8",
	  textTransform: "uppercase",
	  letterSpacing: "0.04em",
	  marginTop: "18px",
	  marginBottom: "8px",
	},
	bucketPrompt: {
	  background: "#fffbeb",
	  border: "1px solid #fde68a",
	  borderRadius: "12px",
	  padding: "12px 16px",
	  fontSize: "13px",
	  color: "#92400e",
	  fontWeight: "600",
	},
	typeCardRow: {
	  display: "grid",
	  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
	  gap: "10px",
	},
	typeCard: {
	  display: "flex",
	  flexDirection: "column",
	  alignItems: "center",
	  gap: "6px",
	  padding: "16px 10px",
	  borderRadius: "14px",
	  border: "1.5px solid #d6deeb",
	  background: "#fff",
	  cursor: "pointer",
	  fontFamily: "inherit",
	  fontSize: "14px",
	  fontWeight: "700",
	  color: "#334155",
	},
	typeCardActive: {
	  border: "1.5px solid #1d4ed8",
	  background: "linear-gradient(135deg, #1d4ed8, #2563eb)",
	  color: "#fff",
	},
	typeCardAge: {
	  fontSize: "11.5px",
	  fontWeight: "600",
	  opacity: 0.8,
	},
	errorText: {
	  color: "#dc2626",
	  fontSize: "14px",
	  fontWeight: "600",
	  marginTop: "12px",
	},
	popupOverlay: {
	  position: "fixed",
	  inset: 0,
	  backgroundColor: "rgba(15, 23, 42, 0.45)",
	  display: "flex",
	  alignItems: "center",
	  justifyContent: "center",
	  zIndex: 50,
	},
	popupCard: {
	  background: "#fff",
	  borderRadius: "18px",
	  padding: "22px 24px",
	  maxWidth: "360px",
	  width: "90%",
	  textAlign: "center",
	  boxShadow: "0 20px 40px rgba(15, 23, 42, 0.25)",
	},
	popupTitle: {
	  margin: "10px 0 6px 0",
	  fontSize: "1.1rem",
	  fontWeight: "800",
	  color: "#065f46",
	},
	popupText: {
	  margin: 0,
	  fontSize: "14px",
	  color: "#4b5563",
	},
	popupButton: {
	  marginTop: "16px",
	  borderRadius: "999px",
	  border: "none",
	  background: "#16a34a",
	  color: "#fff",
	  fontSize: "14px",
	  fontWeight: "700",
	  padding: "10px 24px",
	  cursor: "pointer",
	},
  };

  const handleInputChange = (e) => {
	const { name, value } = e.target;
	const cleanValue =
	  name === "mobilenumber" ? value.replace(/\D/g, "").slice(0, 10) : value;

	setFormData((prev) => {
	  // The care checklist below is informational, not a manual pick-list —
	  // every item in the picked Child Type's bucket (Baby Care or Child
	  // Care) plus every Safety item is included automatically. Picking a
	  // new Child Type just swaps service_type to that bucket's full list.
	  let nextServiceType = {};
	  if (name === "childType") {
		const nextBucket = BABY_BUCKET_TYPES.includes(value)
		  ? "baby" : value === "Adolescent/School-Going Child" ? "child" : null;
		const bucketOptions = nextBucket === "baby" ? BABY_CARE_OPTIONS : nextBucket === "child" ? CHILD_CARE_OPTIONS : [];
		nextServiceType = {
		  service_type: nextBucket
			? [...bucketOptions, ...SAFETY_OPTIONS].map((o) => o.value)
			: [],
		};
	  }
	  return {
		...prev,
		[name]: cleanValue,
		...(name === "city" ? { area: "" } : {}),
		...nextServiceType,
	  };
	});
  };

  // Step 1 -> Step 2: validate the form, then show Terms & Conditions.
  // The actual POST only happens from submitRequirement(), reached via the
  // Terms step's checkbox + Submit button — see TermsAcceptStep.
  const handleNext = (e) => {
	e.preventDefault();
	setSubmitError("");

	if (!careBucket) {
	  setSubmitError("Please select the child's type (Infant/Baby, Toddler, PreSchooler or Adolescent/School-Going Child).");
	  return;
	}

	setStep("terms");
  };

  const submitRequirement = async () => {
	setSubmitError("");

	const activeGroupOptions = careBucket === "baby" ? BABY_CARE_OPTIONS : CHILD_CARE_OPTIONS;
	const detailedServiceList = [
	  ...activeGroupOptions
		.filter((item) => formData.service_type.includes(item.value))
		.map((item) => ({ type: careBucket === "baby" ? "baby-care" : "child-care", key: item.value, value: item.value, label: item.label })),
	  ...SAFETY_OPTIONS
		.filter((item) => formData.service_type.includes(item.value))
		.map((item) => ({ type: "safety", key: item.value, value: item.value, label: item.label })),
	];

	const requestBody = {
	  // Requester's own contact details
	  name: formData.username,           // backend reads "name"
	  mobilenumber: formData.mobilenumber,
	  email: formData.email,
	  address: formData.address,
	  city: formData.city,
	  area: formData.area,
	  language: formData.language,

	  // About the child
	  childName: formData.childName,
	  childAge: formData.childAge,
	  childGender: formData.childGender,
	  childType: formData.childType,
	  parentGuardianName: formData.parentGuardianName,
	  parentGuardianRelation: formData.parentGuardianRelation,

	  // Timing & caretaker preference
	  gender: formData.gender,
	  urgencyStatus: formData.urgencyStatus,
	  duration: formData.duration,
	  shiftDuration: formData.shiftDuration,
	  shiftTime: formData.shiftTime,
	  experienceLevel: formData.experienceLevel,
	  additionalNotes: formData.additionalNotes,
	  salaryOffered: formData.salaryOffered,

	  service_category: categoryKey,
	  selected_city: getUserCity(),
	  service_type: careBucket === "baby" ? "babycare-services" : "childcare-services",
	  service_list: formData.service_type,
	  detailed_service_list: detailedServiceList,
	  service: serviceKey,
	  category_label: categoryLabel,
	  service_label: serviceLabel,
	  terms_accepted: true,
	};

	try {
	  setIsSubmitting(true);

	  const res = await fetch(`${BASE_URL}/api/requirements/baby-caretaker/`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(requestBody),
	  });

	  const data = await res.json();

	  if (res.ok && data?.success !== false) {
		setShowThankYou(true);
		setStep("form");
		setFormData({
		  username: "",
		  mobilenumber: "",
		  email: "",
		  address: "",
		  city: "",
		  area: "",
		  language: "",
		  childName: "",
		  childAge: "",
		  childGender: "",
		  childType: "",
		  parentGuardianName: "",
		  parentGuardianRelation: "",
		  service_type: [],
		  gender: "",
		  urgencyStatus: "",
		  duration: "",
		  shiftDuration: "",
		  shiftTime: "",
		  experienceLevel: "",
		  additionalNotes: "",
		  salaryOffered: "",
		});
	  } else {
		setSubmitError(data?.message || data?.error || "Failed to submit requirement.");
	  }
	} catch (error) {
	  console.error("Submit error:", error);
	  setSubmitError("Network error. Please try again.");
	} finally {
	  setIsSubmitting(false);
	}
  };

  const activeCareOptions = careBucket === "baby" ? BABY_CARE_OPTIONS : careBucket === "child" ? CHILD_CARE_OPTIONS : [];
  const selectedBabyCareLabels = [...activeCareOptions, ...SAFETY_OPTIONS]
	.filter((item) => formData.service_type.includes(item.value))
	.map((item) => item.label);

  return (
	<>
	  {showThankYou && (
		<div style={styles.popupOverlay}>
		  <div style={styles.popupCard}>
			<FaCheckCircle color="#16a34a" size={32} />
			<h3 style={styles.popupTitle}>Thank you!</h3>
			<p style={styles.popupText}>
			  Your requirement has been submitted. We will get back to you
			  shortly.
			</p>
			<button
			  type="button"
			  style={styles.popupButton}
			  onClick={() => {
				setShowThankYou(false);
				navigate("/");
			  }}
			>
			  OK
			</button>
		  </div>
		</div>
	  )}

	  <div style={styles.page}>
		<div style={styles.wrapper}>
		  <div style={styles.hero}>
			<div style={styles.heroBadge}>{serviceLabel}</div>
			<h1 style={styles.heroTitle}>
			  Hire a Baby Caretaker
			</h1>
			<p style={styles.heroText}>
			  Post your baby caretaker requirement with support-service
			  checkboxes, location details, and salary offer.
			</p>
		  </div>

		  {step === "form" && (
		  <form style={styles.formCard} onSubmit={handleNext}>
			  <div style={styles.formHeader}>
				<h2 style={styles.formTitle}>
				  Baby Caretaker Requirement Form
				</h2>
				<p style={styles.formSub}>
				  Fill in your personal details, select required baby care
				  services using checkboxes, and mention your salary offer.
				</p>
			  </div>

			  <div style={styles.body}>
				<div style={styles.section}>
				  <h3 style={styles.sectionTitle}>Your Contact Details</h3>
				  <div style={styles.grid}>
					<div style={styles.field}>
					  <label htmlFor="username" style={styles.label}>
						<FaUser /> Name
					  </label>
					  <input
						id="username"
						style={styles.input}
						type="text"
						name="username"
						value={formData.username}
						onChange={handleInputChange}
						required
					  />
					</div>

					<div style={styles.field}>
					  <label htmlFor="mobilenumber" style={styles.label}>
						<FaPhoneAlt /> Mobile Number
					  </label>
					  <input
						id="mobilenumber"
						style={styles.input}
						type="tel"
						name="mobilenumber"
						value={formData.mobilenumber}
						onChange={handleInputChange}
						pattern="[0-9]{10}"
						minLength={10}
						maxLength={10}
						required
					  />
					</div>

					<div style={styles.field}>
					  <label htmlFor="email" style={styles.label}>
						<FaEnvelope /> Email
					  </label>
					  <input
						id="email"
						style={styles.input}
						type="email"
						name="email"
						value={formData.email}
						onChange={handleInputChange}
						required
					  />
					</div>

					<div style={styles.field}>
					  <label htmlFor="address" style={styles.label}>
						<FaMapMarkerAlt /> Address
					  </label>
					  <input
						id="address"
						style={styles.input}
						type="text"
						name="address"
						value={formData.address}
						onChange={handleInputChange}
						required
					  />
					</div>

					<div style={styles.field}>
					  <label htmlFor="city" style={styles.label}>
						<FaMapMarkerAlt /> City
					  </label>
					  <select
						id="city"
						style={styles.select}
						name="city"
						value={formData.city}
						onChange={handleInputChange}
						required
					  >
						<option value="">Select City</option>
						{Object.keys(areaOptions).map((city) => (
						  <option key={city} value={city}>
							{city.charAt(0).toUpperCase() + city.slice(1)}
						  </option>
						))}
					  </select>
					</div>

					<div style={styles.field}>
					  <label htmlFor="area" style={styles.label}>
						<FaMapMarkerAlt /> Area
					  </label>
					  <select
						id="area"
						style={styles.select}
						name="area"
						value={formData.area}
						onChange={handleInputChange}
						required
						disabled={!formData.city}
					  >
						<option value="">Select Area</option>
						{availableAreas.map((area) => (
						  <option key={area} value={area}>
							{area}
						  </option>
						))}
					  </select>
					</div>

					<div style={styles.field}>
					  <label htmlFor="language" style={styles.label}>
						<FaLanguage /> Preferred Language
					  </label>
					  <input
						id="language"
						style={styles.input}
						type="text"
						name="language"
						placeholder="Hindi, English, Kannada etc"
						value={formData.language}
						onChange={handleInputChange}
						required
					  />
					</div>
				  </div>
				</div>

				<div style={styles.section}>
					<h3 style={styles.sectionTitle}>
						<FaBaby />
					Child Details
					</h3>
					<div style={styles.grid}>
						<div style={styles.field}>
							<label htmlFor="childName" style={styles.label}>
								<FaUser /> Child Name
							</label>
							<input
								id="childName"
								style={styles.input}
								type="text"
								name="childName"
								value={formData.childName}
								onChange={handleInputChange}
								required
							/>
						</div>

						<div style={styles.field}>
							<label htmlFor="childAge" style={styles.label}>
								<FaUser /> Age
							</label>
							<input
								id="childAge"
								style={styles.input}
								type="number"
								name="childAge"
								min="0"
								max="18"
								value={formData.childAge}
								onChange={handleInputChange}
								required
							/>
						</div>

						<div style={styles.field}>
							<label htmlFor="childGender" style={styles.label}>
								<FaUserTag /> Gender
							</label>
							<select
								id="childGender"
								style={styles.select}
								name="childGender"
								value={formData.childGender}
								onChange={handleInputChange}
								required
							>
								<option value="">Select</option>
								<option value="Male">Male</option>
								<option value="Female">Female</option>
								<option value="Others">Others</option>
							</select>
						</div>

						<div style={styles.field}>
							<label htmlFor="parentGuardianName" style={styles.label}>
								<FaUser /> Parent/Guardian Name
							</label>
							<input
								id="parentGuardianName"
								style={styles.input}
								type="text"
								name="parentGuardianName"
								value={formData.parentGuardianName}
								onChange={handleInputChange}
								required
							/>
						</div>

						<div style={styles.field}>
							<label htmlFor="parentGuardianRelation" style={styles.label}>
								<FaUserTag /> Relationship to Child
							</label>
							<select
								id="parentGuardianRelation"
								style={styles.select}
								name="parentGuardianRelation"
								value={formData.parentGuardianRelation}
								onChange={handleInputChange}
								required
							>
								<option value="">Select</option>
								{PARENT_GUARDIAN_RELATIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
							</select>
						</div>
					</div>

					<div style={{ marginTop: "20px" }}>
						<div style={styles.label}>Child Type</div>
						<p style={styles.helper}>This decides which care checklist applies below.</p>
						<div style={styles.typeCardRow}>
							{CHILD_TYPE_OPTIONS.map((t) => (
								<button key={t.value} type="button"
									style={{ ...styles.typeCard, ...(formData.childType === t.value ? styles.typeCardActive : {}) }}
									onClick={() => handleInputChange({ target: { name: "childType", value: t.value } })}
								>
									{t.value === "Adolescent/School-Going Child" ? <FaChild size={20} /> : t.value === "Infant/Baby" ? <FaBaby size={20} /> : <FaUser size={18} />}
									<span>{t.value}</span>
									<span style={styles.typeCardAge}>{t.age}</span>
								</button>
							))}
						</div>
					</div>
				</div>

				<div style={styles.section}>
					<h3 style={styles.sectionTitle}>
						<FaHandsHelping />
					Care Services Included
					</h3>

					{!careBucket ? (
						<p style={styles.bucketPrompt}>Select the child's type above to see the relevant care checklist.</p>
					) : (
						<>
							<p style={styles.helper}>
								Every {careBucket === "baby" ? "Baby Care" : "Child Care"} and Safety service below is included for a {formData.childType}.
							</p>
							<div style={styles.groupHeading}>{careBucket === "baby" ? "Baby Care" : "Child Care"}</div>
							<div style={styles.serviceGrid}>
								{activeCareOptions.map((item) => (
									<div key={item.value} style={styles.serviceCell}>
										<FaCheckCircle color="#16a34a" size={14} />
										<span style={styles.serviceCellLabel}>{item.label}</span>
									</div>
								))}
							</div>

							<div style={styles.groupHeading}><FaShieldAlt style={{ marginRight: 4 }} />Safety</div>
							<div style={styles.serviceGrid}>
								{SAFETY_OPTIONS.map((item) => (
									<div key={item.value} style={styles.serviceCell}>
										<FaCheckCircle color="#16a34a" size={14} />
										<span style={styles.serviceCellLabel}>{item.label}</span>
									</div>
								))}
							</div>
						</>
					)}
				</div>

				<div style={styles.section}>
					<h3 style={styles.sectionTitle}><FaClock /> Timing &amp; Caretaker Preference</h3>
					<div style={styles.grid}>
						<div style={styles.field}>
							<label htmlFor="gender" style={styles.label}>
								<FaUserTag /> Preferred Caretaker Gender
							</label>
							<select
								id="gender"
								style={styles.select}
								name="gender"
								value={formData.gender}
								onChange={handleInputChange}
								required
							>
								<option value="">Select</option>
								<option value="Male">Male</option>
								<option value="Female">Female</option>
							</select>
						</div>

						<div style={styles.field}>
							<label htmlFor="urgencyStatus" style={styles.label}>
								<FaClock /> Urgency
							</label>
							<select
								id="urgencyStatus"
								style={styles.select}
								name="urgencyStatus"
								value={formData.urgencyStatus}
								onChange={handleInputChange}
								required
							>
								<option value="">Select</option>
								<option value="within_days">Within Days</option>
								<option value="within_months">Within Months</option>
								<option value="just_enquiry">Just Enquiry</option>
							</select>
						</div>

						<div style={styles.field}>
							<label htmlFor="duration" style={styles.label}>
								<FaClock /> Duration
							</label>
							<input
								id="duration"
								style={styles.input}
								type="text"
								name="duration"
								placeholder="In months"
								value={formData.duration}
								onChange={handleInputChange}
								required
							/>
						</div>

						<div style={styles.field}>
							<label htmlFor="shiftTime" style={styles.label}>
								<FaClock /> Shift
							</label>
							<select
								id="shiftTime"
								style={styles.select}
								name="shiftTime"
								value={formData.shiftTime}
								onChange={handleInputChange}
								required
							>
								<option value="">Select</option>
								<option value="morning">Morning</option>
								<option value="afternoon">Afternoon</option>
								<option value="evening">Evening</option>
								<option value="full_day">Full Day</option>
							</select>
						</div>

						<div style={styles.field}>
							<label htmlFor="shiftDuration" style={styles.label}>
								<FaClock /> Hours
							</label>
							<select
								id="shiftDuration"
								style={styles.select}
								name="shiftDuration"
								value={formData.shiftDuration}
								onChange={handleInputChange}
								required
							>
								<option value="">Select</option>
								<option value="2hrs">2 Hours</option>
								<option value="4hrs">4 Hours</option>
								<option value="6hrs">6 Hours</option>
								<option value="12hrs">12 Hours</option>
								<option value="live_24hrs">Live-in (24 Hours)</option>
							</select>
						</div>

						<div style={{ ...styles.field, position: "relative" }}>
							<label htmlFor="experienceLevel" style={styles.label}>
								<FaUserTag /> Caretaker Experience
								<button
									type="button"
									style={styles.infoBtn}
									onClick={() => setShowExperienceInfo((v) => !v)}
									aria-label="What do these experience levels mean?"
								>
									<FaInfoCircle />
								</button>
							</label>
							<select
								id="experienceLevel"
								style={styles.select}
								name="experienceLevel"
								value={formData.experienceLevel}
								onChange={handleInputChange}
								required
							>
								<option value="">Select</option>
								<option value="expert">Expert</option>
								<option value="normal">Normal</option>
								<option value="assistance">Assistance</option>
							</select>
							{showExperienceInfo && (
								<div style={styles.infoPopover}>
									<p style={{ margin: "0 0 8px", fontSize: "13px", lineHeight: 1.6, color: "#374151" }}><strong>Expert:</strong> Highly experienced, trained professional for complex or specialised care needs.</p>
									<p style={{ margin: "0 0 8px", fontSize: "13px", lineHeight: 1.6, color: "#374151" }}><strong>Normal:</strong> Standard experienced caretaker for everyday, routine care.</p>
									<p style={{ margin: 0, fontSize: "13px", lineHeight: 1.6, color: "#374151" }}><strong>Assistance:</strong> Helper-level support for basic day-to-day tasks.</p>
								</div>
							)}
						</div>

						<div style={styles.field}>
							<label htmlFor="salaryOffered" style={styles.label}>
								<FaMoneyBillWave /> Salary Offer
							</label>
							<input
								id="salaryOffered"
								style={styles.input}
								type="text"
								name="salaryOffered"
								placeholder="Per month, e.g. 15000 rs/month"
								value={formData.salaryOffered}
								onChange={handleInputChange}
								required
							/>
						</div>
					</div>
				</div>

				<div style={styles.section}>
				  <h3 style={styles.sectionTitle}>
					<FaInfoCircle />
					Summary & Notes
				  </h3>
				  <p style={styles.helper}>
					Review selected baby care services and add extra care
					instructions if needed.
				  </p>

				  <div
					style={{
					  marginTop: "12px",
					  fontSize: "14px",
					  color: "#0f172a",
					}}
				  >
					{selectedBabyCareLabels.length > 0 && (
					  <p>
						<strong>Selected Services:</strong>{" "}
						{selectedBabyCareLabels.join(", ")}
					  </p>
					)}

					{formData.salaryOffered && (
					  <p>
						<strong>Salary Offer:</strong> {formData.salaryOffered}
					  </p>
					)}
				  </div>

				  <div style={{ marginTop: "14px" }}>
					<label htmlFor="additionalNotes" style={styles.label}>
					  <FaInfoCircle /> Notes
					</label>
					<textarea
					  id="additionalNotes"
					  style={styles.textarea}
					  name="additionalNotes"
					  placeholder="Mention baby age, feeding timing, sleeping pattern, diaper schedule, special care instructions, etc."
					  value={formData.additionalNotes}
					  onChange={handleInputChange}
					/>
				  </div>

				  {submitError && <p style={styles.errorText}>{submitError}</p>}
				</div>

				<div style={styles.submitWrap}>
				  <button
					type="submit"
					style={styles.submitBtn}
					disabled={isSubmitting}
				  >
					Next
				  </button>
				</div>
			  </div>
			</form>
			)}

			{step === "terms" && (
			  <div style={styles.formCard}>
				<TermsAcceptStep
				  onBack={() => setStep("form")}
				  onSubmit={submitRequirement}
				  isSubmitting={isSubmitting}
				  submitError={submitError}
				  serviceLabel={serviceLabel}
				/>
			  </div>
			)}
		</div>
	  </div>
	</>
  );
};

export default BabyCaretaker;